import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { APP_NAME, NAME_MAX, TITLE_MAX } from '@sellit/shared';
import { api, ApiError } from '../lib/api';
import { PoweredBy, Wordmark } from '../lib/Brand';
import { setPageTitle } from '../lib/format';
import { Icon } from '../lib/Icon';

const THANKS = "Thanks! We've received your request. We'll be in touch to help you get your sale started.";

// The Start Selling form. It sends a request for an admin to review; it doesn't create an account.
// Used in the homepage pop-up and on /start (for shared links and browsers without <dialog>).
export function StartSellingForm({ onDone }: { onDone?: () => void }) {
  const [form, setForm] = useState({ name: '', email: '', saleName: '', website: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [sent, setSent] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setFields({});
    try {
      // Success shows only once the server has saved the request; on failure the typed text stays.
      await api.requestToSell(form);
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError && err.status !== 0 ? err.message : "We couldn't send your request. Check your connection and try again.");
      if (err instanceof ApiError && err.fields) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="success" role="status">
        <p className="success-mark"><Icon name="check" size={34} /></p>
        <p className="start-thanks">{THANKS}</p>
        {onDone ? (
          <button type="button" className="btn" onClick={onDone}>Done</button>
        ) : (
          <Link className="btn" to="/">Back to {APP_NAME}</Link>
        )}
      </div>
    );
  }

  return (
    <form className="stack" onSubmit={submit} noValidate>
      <label className="field">
        <span>Your Name</span>
        <input value={form.name} maxLength={NAME_MAX} autoComplete="name" required onChange={(e) => setForm({ ...form, name: e.target.value })} />
        {fields.name && <small className="error">{fields.name}</small>}
      </label>
      <label className="field">
        <span>Email Address</span>
        <input type="email" inputMode="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        {fields.email && <small className="error">{fields.email}</small>}
      </label>
      <label className="field">
        <span>Sale Name (optional)</span>
        <input value={form.saleName} maxLength={TITLE_MAX} placeholder="e.g. Garcia family moving sale" onChange={(e) => setForm({ ...form, saleName: e.target.value })} />
      </label>
      <div className="hp" aria-hidden="true">
        <label>
          Website
          <input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
        </label>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      <button className="btn primary big" disabled={busy}>{busy ? 'Sending…' : "Let's Get Started"}</button>
      <p className="muted small">
        We'll use your name and email only to respond to your request and help you set up your sale. We don't sell your
        information. <Link to="/privacy">Privacy Policy</Link>
      </p>
    </form>
  );
}

/** A "Start Selling — It's Free" button that opens the form in a pop-up on the current page. */
export function StartSellingButton() {
  const dialog = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  const titleId = useId();
  // Typed text survives closing and reopening; after a successful send the next open starts fresh.
  const [key, setKey] = useState(0);

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      // Start in the first field (showModal would otherwise focus the close button).
      d.querySelector<HTMLInputElement>('input')?.focus();
    }
    if (!open && d.open) d.close();
  }, [open]);

  return (
    <>
      <button type="button" className="btn primary big" onClick={() => setOpen(true)}>Start Selling — It's Free</button>
      <dialog
        ref={dialog}
        className="start-dialog"
        aria-labelledby={titleId}
        onClose={() => setOpen(false)}
        // A tap on the dimmed backdrop (outside the panel) closes it.
        onClick={(e) => e.target === e.currentTarget && setOpen(false)}
      >
        <div className="start-panel">
          <button type="button" className="start-close" aria-label="Close" onClick={() => setOpen(false)}>×</button>
          <h2 id={titleId}>Start Selling</h2>
          <p className="muted">Ready to sell? Let's get you started.</p>
          <StartSellingForm key={key} onDone={() => { setOpen(false); setKey((k) => k + 1); }} />
        </div>
      </dialog>
    </>
  );
}

export function StartSellingPage() {
  useEffect(() => setPageTitle(`Start Selling · ${APP_NAME}`), []);
  return (
    <>
      <main className="page narrow start-page">
        <Link to="/" className="policy-brand" aria-label={`${APP_NAME} home`}><Wordmark className="wordmark" /></Link>
        <h1>Start Selling</h1>
        <p className="landing-lede">Ready to sell? Let's get you started.</p>
        <StartSellingForm />
      </main>
      <PoweredBy showLogin />
    </>
  );
}
